import { Component, ChangeDetectionStrategy, computed, inject, signal, effect, output, input, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

// Tier 2: removed Wijmo `CollectionView`, `wjcGrid.FlexGrid`, `WjGridModule`, `WjGridFilterModule`.
// Replaced by AG Grid via KiteGridComponent (already in WidgetsModule) + manual computed
// signal-based filter + slice pagination.

import { SharedModule } from '../../../../../layout/shared/shared.module';
import { KiteModule } from '../../../../../shared/widgets/kite/kite.module';
import { ResumeDataService } from '../../services/resume-data.service';
import { WidgetsModule } from '../../../../../shared/widgets.module';
import { ClientsService } from '../../../../../services/customers/clients.service';
import { DirectivesModule } from '../../../../../directives/directives.module';

// Tier 2 imports: wrapper + factory column defs.
import {
  createActionColumn,
  createBadgeColumn,
  createDateColumn,
  createPlainColumn
} from '../../../../../shared/components/kite-grid/column-utils/column-utils';

@Component({
    selector: 'app-customer-orders-tab',
    imports: [CommonModule, SharedModule, KiteModule, WidgetsModule, DirectivesModule
        // WjGridModule + WjGridFilterModule removed (Tier 2.1).
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './customer-orders-tab.component.html'
})
export class CustomerOrdersTabComponent {

  private readonly cdr = inject(ChangeDetectorRef);

  readonly resumeData = inject(ResumeDataService);
  readonly clientsService = inject(ClientsService);
  private readonly datePipe = inject(DatePipe);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);

  readonly isVisible = input.required<boolean>();
  readonly isLoading = signal(false);
  readonly orderClicked = output<any>();
  readonly tabletSearchTerm = signal('');
  // Tier 2: `orders` is now a plain array signal (was: CollectionView).
  readonly orders = signal<any[]>([]);
  readonly statsForm = signal<any>({
    tank_install_date: '',
    order_install: '',
    total_orders: '',
    pending_orders: '',
    close_orders: ''
  });
  readonly pageSize = 10;
  readonly currentPage = signal(1);

  // Computed: applies the search filter (replaces legacy `CollectionView.filter`).
  readonly filteredOrders = computed(() => {
    const term = this.tabletSearchTerm().toLowerCase();
    const data = this.orders();
    if (!term) return data;
    return data.filter((item: any) =>
      item.ident_order?.toLowerCase().includes(term) ||
      item.type?.toLowerCase().includes(term) ||
      item.status?.toLowerCase().includes(term) ||
      item.name_servwork?.toLowerCase().includes(term) ||
      item.name_installation?.toLowerCase().includes(term)
    );
  });

  // Computed: paged slice for the wrapper (replaces legacy `CollectionView.moveToPage`).
  readonly pagedOrders = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize;
    return this.filteredOrders().slice(start, start + this.pageSize);
  });

  columnDefs: any[] = [];

  constructor() {
    this.columnDefs = [
      // Row-header action: opens the order detail (was the only consumer of the
      // legacy global (click) handler that did grid.hitTest).
      createActionColumn({
        width: 60,
        buttons: [
          {
            icon: 'fa-info-circle',
            title: 'View order detail',
            ariaLabel: 'view order detail',
            classes: 'view-order-btn'
          }
        ]
      }),
      createPlainColumn({
        field: 'ident_order',
        headerName: 'Order ID',
        flex: 1,
        cellClass: 'ag-cell-center'
      }),
      createPlainColumn({
        field: 'type',
        headerName: 'Order Type',
        flex: 1,
        cellClass: 'ag-cell-center'
      }),
      // Due Date: usa start_date (fecha programada/requerida).
      // end_service solo existe en órdenes completadas; para CS nuevas está vacío.
      createDateColumn({
        field: 'start_date',
        headerName: 'Due Date',
        flex: 1,
        cellClass: 'ag-cell-center',
        format: 'EEEE, MMMM d, y h:mm a'
      }),
      // Status como badge visual con colores por estado.
      createBadgeColumn({
        field: 'status',
        headerName: 'Status',
        width: 130,
        dotClass: (item: any) => this.getStatusDotClass(item?.status),
        text: (item: any) => item?.status ?? ''
      })
    ];

    effect(() => {
      const clientOrdersData = this.resumeData.clientOrdersTab();
      if (clientOrdersData && this.isVisible()) {
        // Tier 2: assign plain array (was: `new CollectionView(...)`).
        this.orders.set(clientOrdersData.orders ?? []);
        this.statsForm.set({
          tank_install_date: clientOrdersData.tank_install_date
            ? this.datePipe.transform(clientOrdersData.tank_install_date, 'EEEE, MMMM d, y h:mm a')
            : 'N/A',
          order_install: clientOrdersData.order_install,
          total_orders: clientOrdersData.total_orders,
          pending_orders: clientOrdersData.pending_orders,
          close_orders: clientOrdersData.close_orders
        });
        this.cdr.detectChanges();
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    const customerId = this.route.snapshot.paramMap.get('id') ?? '';
    this.isLoading.set(true);
    this.clientsService.getClientOrdersInformation(customerId)
      .subscribe({
        next: (data: any) => {
          this.resumeData.clientOrdersTab.set(data.data);
          this.isLoading.set(false);
        },
        error: (error: any) => {
          this.toastr.error(error.error.msg || 'An error occurred while fetching orders', 'Error');
          this.isLoading.set(false);
        }
      });
  }

  onSearch(term: string): void {
    this.tabletSearchTerm.set(term);
    // Reset to first page so the user sees matching results immediately.
    this.currentPage.set(1);
  }

  /** Tier 2: replaces `gridClickHandler(e, grid)` which used `grid.hitTest(e)`.
   *  The AG Grid (cellClicked) output fires for any cell click; the row data
   *  is directly available via `event.data`. The original handler also ran
   *  on the row-header icon click — kept here via the `view-order-btn` class
   *  delegation as a safety net but no longer required (AG Grid cellClicked
   *  fires for any cell). */
  onCellClicked(event: any): void {
    if (!event?.data) return;
    this.openOrderDetail(event.data);
  }

  /** Emite el _id de la orden para que el padre (ResumeComponent) abra
   *  OrderInfoModalComponent — el mismo modal que usa Manage Orders.
   *  Se evita construir un objeto reducido con campos de completion
   *  (start_service, end_service…) que están vacíos para órdenes CS nuevas. */
  private openOrderDetail(dataRow: any): void {
    this.orderClicked.emit(dataRow._id);
  }

  /** Devuelve la clase de punto (dot) para el badge de estado.
   *  Usa las mismas clases CSS globales que el resto del sistema
   *  (definidas en orders-calendar.component.scss y BadgeCellRendererComponent). */
  getStatusDotClass(status?: string): string {
    switch (status) {
      case 'CLOSE':       return 'dot-complete';
      case 'PARTIAL':     return 'dot-partial';
      case 'Assigned':    return 'dot-info';
      case 'New':         return 'dot-unassigned';
      case 'Unassigned':  return 'dot-warning';
      default:            return 'dot-unassigned';
    }
  }

  refreshData(page: number): void {
    // Tier 2: was `this.orders()?.moveToPage(page - 1)`.
    this.currentPage.set(page);
  }

  /** Navega a la página de creación de órdenes con el cliente actual preseleccionado. */
  navigateToCreateOrder(): void {
    const customerId = this.route.snapshot.paramMap.get('id') ?? '';
    this.router.navigate(['/uco/create-order'], { queryParams: { clientId: customerId } });
  }
}
